import React from 'react'
import { Router, routerObj, router } from './components/routing/Router'
import { Provider } from 'react-redux'
import { ChakraProvider } from '@chakra-ui/react'
import '@fontsource-variable/inter'
import { store } from './store'
import { InvoicesService } from './services/Invoices'
import { SubscriptionService } from './services/Subscription'
import { UserRole } from './ts/interfaces/interfaces'
import { ErrorBoundary } from './pages/ErrorBoundary'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { ZummaProvider, ZummaTexts } from '@zumma/ui'
import { useTranslation } from 'react-i18next'
import { DownloadProgressOverlay } from './components/ui/DownloadProgressOverlay'
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (antes cacheTime)
      refetchOnWindowFocus: false,
      retry: 1
    }
  }
})

function App() {
  const { t } = useTranslation()
  const zummaTexts = React.useMemo<Partial<ZummaTexts>>(
    () => ({
      selectPlaceholder: t('uiKit.selectPlaceholder'),
      searchPlaceholder: t('uiKit.searchPlaceholder'),
      moreSelected: (count) => t('uiKit.moreSelected', { n: count }),
      confirm: t('uiKit.confirm'),
      cancel: t('common.cancel'),
      dropFile: t('uiKit.dropFile'),
      upload: t('uiKit.upload'),
      fieldDisabled: t('uiKit.fieldDisabled'),
      dropFileOrClick: t('uiKit.dropFileOrClick'),
      dropFilesOrClick: t('uiKit.dropFilesOrClick'),
      maxFileSize: (sizeMB) => t('uiKit.maxFileSize', { size: sizeMB }),
      fileTooLarge: (sizeMB) => t('uiKit.fileTooLarge', { size: sizeMB }),
      validationError: t('uiKit.validationError'),
      loadingProgress: (progress) => t('uiKit.loadingProgress', { progress }),
      removeFile: t('uiKit.removeFile')
    }),
    [t]
  )
  const init = async () => {
    if (localStorage.getItem('user')) {
      await SubscriptionService({ useLoader: true }).getSubscriptionStatus()
      if (store.getState().user.role === UserRole.ADMIN && ![routerObj.signIn.path, routerObj.signUp.path, routerObj.renewPassword.cleanParamsPath, routerObj.recoverPassword.path].includes(router.state.location.pathname)) {
        await InvoicesService({ useLoader: true }).getInvoicesStatus({ fetch_stats: false })
      }
    }
  }

  React.useEffect(() => {
    init()
  }, [])

  return (
    <QueryClientProvider client={queryClient}>
      <ZummaProvider texts={zummaTexts}>
        <Provider store={store}>
          <Router />
          <DownloadProgressOverlay />
          <ToastContainer
            position='bottom-center'
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme='light'
          />
        </Provider>
      </ZummaProvider>
    </QueryClientProvider>
  )
}

export default App
